import {
  IconButton,
  usePopupState,
  bindTrigger,
  SelectAdvancePopover,
  SelectAdvancePopoverProps,
  bindPopover,
  SelectAdvanceComponents,
  Typography,
  Box,
  Badge,
  BadgeProps,
  IconButtonProps,
} from '@applift/factor'
import { ViewColumn } from '@applift/icons'
import { ColumnDef } from '../../Datagrid'
import { VisibilityState } from '../../models/gridReactTable'

interface LabelValuePair {
  label: string
  value: string
  subRows?: undefined | LabelValuePair[]
}

interface ColumnVisibilityProps {
  data: LabelValuePair[]
  onChange: SelectAdvancePopoverProps<LabelValuePair>['onChange']
  value: SelectAdvancePopoverProps<LabelValuePair>['value']
  width?: number
  slotProps?: {
    BadgeProps?: BadgeProps
    IconButtonProps?: IconButtonProps
    SelectAdvancePopoverProps?: SelectAdvancePopoverProps<LabelValuePair>
  }
}

export const ColumnVisibility = (props: ColumnVisibilityProps) => {
  const { data, onChange, value, width = 300, slotProps } = props

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'columnSelector',
  })

  const listItem =
    SelectAdvanceComponents.DefaultListOptionItemWrapper<LabelValuePair>({
      disableCheckbox: true,
      disableRowExpansion: true,
      selectionStyle: 'all',
    })

  return (
    <>
      <Badge
        badgeContent={Object.values(value).filter(Boolean).length}
        overlap="circular"
        color="secondary"
        {...slotProps?.BadgeProps}
      >
        {/* @ts-ignore */}
        <IconButton
          {...bindTrigger(popupState)}
          selected={popupState.isOpen}
          {...slotProps?.IconButtonProps}
        >
          <ViewColumn />
        </IconButton>
      </Badge>
      {/* @ts-ignore */}
      <SelectAdvancePopover
        {...bindPopover(popupState)}
        multiple
        defaultExpanded={true}
        data={data}
        onChange={onChange}
        hideSearch
        value={value}
        rowCount={data.length}
        renderListItem={listItem}
        renderContentTop={
          <Box sx={{ px: 12, pt: 12, pb: 4 }}>
            <Typography
              component="h2"
              variant="bodySmall"
              weight="demi"
              gutterBottom={false}
            >
              Columns
            </Typography>
          </Box>
        }
        renderOption={({ row }) => {
          if (row.subRows.length > 0) {
            return (
              <Typography
                component="h2"
                variant="bodySmall"
                weight="demi"
                gutterBottom={false}
              >
                {row.original.label}
              </Typography>
            )
          } else {
            return (
              <Typography
                component={'span'}
                variant="bodySmall"
                gutterBottom={false}
              >
                {row.original.label}
              </Typography>
            )
          }
        }}
        getRowCanExpand={row => (row.subRows?.length ?? 0) > 0}
        getSubRows={row => row.subRows}
        {...slotProps?.SelectAdvancePopoverProps}
        slotProps={{
          ...slotProps?.SelectAdvancePopoverProps?.slotProps,
          PaperProps: {
            ...slotProps?.SelectAdvancePopoverProps?.slotProps?.PaperProps,
            style: {
              ...slotProps?.SelectAdvancePopoverProps?.slotProps?.PaperProps
                ?.style,
              maxWidth: width,
            },
          },
        }}
      />
    </>
  )
}

export const constructListFromColumnDef = <T,>(
  coldef: ColumnDef<T, unknown>[]
): [LabelValuePair[], VisibilityState] => {
  const accumulator: LabelValuePair[] = []
  let initialSelection: VisibilityState = {}
  if (!Array.isArray(coldef)) {
    return [accumulator, initialSelection]
  }
  for (let itr = 0; itr < coldef.length; itr++) {
    const currColDef = coldef[itr]
    if (currColDef) {
      // @ts-ignore
      const accessorKey = currColDef.accessorKey

      const value =
        currColDef.id ??
        (accessorKey ? accessorKey.replace('.', '_') : undefined) ??
        (typeof currColDef.header === 'string' ? currColDef.header : undefined)

      const label =
        typeof currColDef?.meta?.headerTitle === 'string' &&
        currColDef?.meta?.headerTitle !== ''
          ? currColDef?.meta?.headerTitle
          : typeof currColDef.header === 'string'
            ? currColDef.header
            : typeof accessorKey === 'string'
              ? accessorKey.replace('.', '_')
              : typeof currColDef.id === 'string'
                ? currColDef.id
                : ''

      const obj: LabelValuePair = {
        // @ts-ignore
        label,
        value,
      }
      // @ts-ignore
      if (Array.isArray(currColDef.columns)) {
        // @ts-ignore
        const result = constructListFromColumnDef(currColDef.columns)
        obj.subRows = result[0]
        initialSelection = { ...initialSelection, ...result[1] }
      }

      if (currColDef.meta?.excludeColumnFromColumnVisibility !== true) {
        accumulator.push(obj)
        if ((obj.subRows?.length ?? 0) <= 0) {
          initialSelection = {
            ...initialSelection,
            [value]: currColDef?.meta?.defaultVisible ?? true,
          }
        }
      }
    }
  }
  return [accumulator, initialSelection]
}
